import {Response} from 'superagent';
import {handleResponseErrors, RequestMethod, sendMultipartRequest, sendRequest} from "../../agent";

export type ImportEntityType = "patient" | "doctor";

export interface ImportSourceRow {
    [key: string]: string;
}

export interface ImportMapping {
    [sourceColumn: string]: string;
}

export interface ImportUploadResponse {
    importSessionId: string;
    entityType: ImportEntityType;
    status: string;
    detectedColumns: string[];
    sampleRows: ImportSourceRow[];
    totalRows: number;
    fileFormat: string;
    fileName: string;
    message: string;
    mapping?: ImportMapping;
}

export interface ImportTargetField {
    value: string;
    text: string;
    required?: boolean;
}

export type ImportAliasMap = Record<string, string[]>;

export interface ImportMetaResponse {
    entityType: ImportEntityType;
    fields: ImportTargetField[];
    aliases?: ImportAliasMap;
}

export interface ImportSessionResponse extends Omit<ImportUploadResponse, "message"> {
    fileSize: number;
    mapping?: ImportMapping;
    createdAt: string;
    updatedAt: string;
}

export interface ImportExecuteResponse {
    taskId: string;
    importSessionId: string;
    message: string;
}

export interface ImportExecuteRequest {
    importSessionId: string;
    entityType: ImportEntityType;
    mapping: ImportMapping;
    options?: {
        batchSize?: number;
        skipErrors?: boolean;
        dryRun?: boolean;
        detailedLog?: boolean;
        defaultDoctorId?: string;
        restoreDeleted?: boolean;
    };
}

const callImportApi = <TResult>(method: RequestMethod, endpoint: string, data?: object): Promise<TResult | undefined> => {
    return sendRequest(method, endpoint, data ?? {})
        .then((response) => response.body as TResult)
        .catch((error: any) => handleResponseErrors(error));
};

export interface ImportMappingConfig {
    _id: string;
    name: string;
    entityType: ImportEntityType;
    mapping: ImportMapping;
    createdBy?: string;
    createdAt: string;
    updatedAt: string;
}

export interface ImportMappingConfigRequest {
    name: string;
    entityType: ImportEntityType;
    mapping: ImportMapping;
}

export class ImportDataProvider {
    upload(file: File, entityType: ImportEntityType): Promise<ImportUploadResponse | undefined> {
        return sendMultipartRequest("post", "/import/upload", "file", file, {entityType})
            .then((response: Response) => response.body as ImportUploadResponse)
            .catch((error: any) => handleResponseErrors(error));
    }

    getMeta(entityType: ImportEntityType): Promise<ImportMetaResponse | undefined> {
        return callImportApi<ImportMetaResponse>('get', `/import/fields/${entityType}`);
    }

    getSession(importSessionId: string): Promise<ImportSessionResponse | undefined> {
        return callImportApi<ImportSessionResponse>('get', `/import/session/${importSessionId}`);
    }

    getConfigs(entityType: ImportEntityType): Promise<ImportMappingConfig[] | undefined> {
        return callImportApi<ImportMappingConfig[]>('get', `/import/configs/${entityType}`);
    }

    getConfig(configId: string): Promise<ImportMappingConfig | undefined> {
        return callImportApi<ImportMappingConfig>('get', `/import/config/${configId}`);
    }

    saveConfig(payload: ImportMappingConfigRequest): Promise<ImportMappingConfig | undefined> {
        return callImportApi<ImportMappingConfig>('post', `/import/config`, payload);
    }

    updateConfig(configId: string, payload: Omit<ImportMappingConfigRequest, "name">): Promise<ImportMappingConfig | undefined> {
        return callImportApi<ImportMappingConfig>('put', `/import/config/${configId}`, payload);
    }

    deleteConfig(configId: string): Promise<{success: boolean} | undefined> {
        return callImportApi<{success: boolean}>('delete', `/import/config/${configId}`);
    }

    execute(requestData: ImportExecuteRequest): Promise<ImportExecuteResponse | undefined> {
        return callImportApi<ImportExecuteResponse>('post', '/import/execute', requestData);
    }
}
